import { useState } from "react";
import { useTranslation } from "react-i18next";
import { needsTranslation, translateText } from "../api/translate";

interface Props {
  text: string;
  /** Tailwind classes for the rendered text element (not the button). */
  className?: string;
  /** "p" or "span" — defaults to "span" so callers can compose inline. */
  as?: "p" | "span" | "div" | "h1" | "h2" | "h3";
}

/**
 * Renders user-generated text and, if it appears to be in a different
 * language than the current UI language, shows a tiny "Translate" button
 * beneath it. On click the button POSTs to /api/public/translate (a
 * Google Translate proxy on the bot) and swaps the original text for
 * the translation. A second click toggles back to the original.
 *
 * The button is rendered as a separate inline element below the text so
 * that screen layout doesn't reflow when toggling.
 */
export function TranslatableText({ text, className, as: Tag = "span" }: Props) {
  const { i18n, t } = useTranslation();
  const uiLang = i18n.language.split("-")[0] ?? "ru";
  const show = needsTranslation(text, uiLang);

  const [translated, setTranslated] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);

  async function toggle() {
    if (translated) {
      setTranslated(null);
      return;
    }
    setBusy(true);
    setErr(false);
    try {
      const result = await translateText(text, uiLang);
      setTranslated(result);
    } catch {
      setErr(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Tag className={className}>{translated ?? text}</Tag>
      {show && (
        <button
          type="button"
          onClick={() => void toggle()}
          disabled={busy}
          className="inline-flex items-center gap-1 mt-1 text-[11px] font-semibold text-brand-purple hover:underline underline-offset-2 disabled:opacity-60"
        >
          <GlobeIcon />
          {err
            ? t("translate.failed")
            : busy
              ? t("translate.loading")
              : translated
                ? t("translate.showOriginal")
                : t("translate.button")}
        </button>
      )}
    </>
  );
}

function GlobeIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20" />
      <path d="M12 2a15.3 15.3 0 0 1 0 20" />
      <path d="M12 2a15.3 15.3 0 0 0 0 20" />
    </svg>
  );
}
